import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { isOvertime } from '@obraplan/core/domain/planning/capacity-calculator';
import {
  CHANGE_KIND_LABEL,
  CHANGE_SOURCE_LABEL,
  MIN_CHANGE_JUSTIFICATION,
  type PlanningChange,
} from '@obraplan/core/domain/planning/planning-change';
import { Hours, QUARTERS_PER_HOUR } from '@obraplan/core/domain/shared/hours';
import { formatQuantity, type UnitOfMeasure } from '@obraplan/core/domain/schedule/unit-of-measure';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { quandoFoiFeita } from './fila-da-conferencia';

/**
 * Ajuste da conferência — com a justificativa **exigida quando o total passa da jornada**.
 *
 * A hora extra é a regra que mais atrapalha quem está no canteiro se for descoberta tarde:
 * digitar as horas, tocar em salvar e receber "justificativa obrigatória" é o caminho que
 * ensina a odiar o sistema. Aqui o campo já aparece marcado como obrigatório assim que o
 * total do dia — as horas desta frente mais as das outras da mesma pessoa — passa da
 * jornada, e acompanha o que está sendo digitado: a tela **sabe** a jornada antes de abrir
 * (RN-292).
 *
 * A tela avisa; quem recusa continua sendo o caso de uso (RN-134).
 */
export interface AdjustDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceName: string;
  activityLabel: string;
  currentQuarterHours: number;
  /** A unidade da frente. Ausente = frente não medida, e o campo de medição não aparece. */
  unit?: UnitOfMeasure;
  /** Meta do dia, para o conferente ver contra o que está medindo. */
  plannedQuantity?: number;
  /** Medição já lançada, quando a conferência está sendo refeita. */
  executedQuantity?: number;
  /**
   * A jornada deste dia para esta pessoa, em quartos de hora. Ausente = sem limite
   * (RN-047): o motivo nunca é exigido.
   */
  capacityQuarterHours?: number;
  /** As horas da mesma pessoa **neste dia, em outras frentes** — entram no total (RN-292). */
  otherQuarterHours?: number;
  isPending: boolean;
  errorMessage?: string;
  onConfirm: (input: {
    quarterHours: number;
    justification?: string;
    executedQuantity?: number;
  }) => void;
}

export function AdjustDialog({
  open,
  onOpenChange,
  resourceName,
  activityLabel,
  currentQuarterHours,
  unit,
  plannedQuantity,
  executedQuantity,
  capacityQuarterHours,
  otherQuarterHours = 0,
  isPending,
  errorMessage,
  onConfirm,
}: AdjustDialogProps) {
  const [hours, setHours] = useState('');
  const [justification, setJustification] = useState('');
  const [quantity, setQuantity] = useState('');

  useEffect(() => {
    if (!open) return;
    setHours(String(currentQuarterHours / QUARTERS_PER_HOUR));
    setJustification('');
    /*
     * A medição abre com o que já foi lançado, ou com a **meta** quando ainda não houve
     * medição: no dia comum saiu o combinado, e obrigar a redigitar o número que já está
     * na tela é o caminho mais curto para ninguém medir. Quem produziu diferente
     * sobrescreve — que é o caso raro, e é o que merece o trabalho.
     */
    setQuantity(
      executedQuantity !== undefined
        ? String(executedQuantity)
        : plannedQuantity !== undefined
          ? String(plannedQuantity)
          : '',
    );
  }, [open, currentQuarterHours, plannedQuantity, executedQuantity]);

  const parsed = Math.round(Number(hours.replace(',', '.')) * QUARTERS_PER_HOUR);
  // Zero não é ajuste: é ausência (do dia) ou remoção da alocação (da frente). O
  // domínio recusa `0`, e deixar o botão ativo só levaria a uma recusa depois do toque.
  const validHours = Number.isFinite(parsed) && parsed > 0;
  // RN-292 — hora extra: o total do dia, com o que está digitado, passou da jornada.
  const required = validHours && isOvertime(otherQuarterHours + parsed, capacityQuarterHours);
  const validReason = !required || justification.trim().length >= MIN_CHANGE_JUSTIFICATION;

  const parsedQuantity = quantity.trim() === '' ? undefined : Number(quantity.replace(',', '.'));
  const validQuantity =
    parsedQuantity === undefined || (Number.isFinite(parsedQuantity) && parsedQuantity >= 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajustar o que foi executado</DialogTitle>
          <DialogDescription>
            {resourceName} · {activityLabel} · planejado{' '}
            {Hours.ofQuarters(currentQuarterHours).format()}
          </DialogDescription>
        </DialogHeader>

        {required && (
          <p className="flex items-start gap-2 rounded-md border border-(--color-state-warn) bg-(--color-surface-muted) p-3 text-sm">
            <AlertTriangle
              className="mt-0.5 size-4 shrink-0 text-(--color-state-warn)"
              aria-hidden="true"
            />
            <span>
              <strong>Hora extra — justificativa obrigatória.</strong> O total do dia,{' '}
              {Hours.ofQuarters(otherQuarterHours + parsed).format()}, passa da jornada de{' '}
              {Hours.ofQuarters(capacityQuarterHours ?? 0).format()}. O motivo fica registrado com o
              seu nome.
            </span>
          </p>
        )}

        <div className="flex flex-col gap-4">
          <Field
            label="Horas executadas"
            htmlFor="adjust-hours"
            required
            hint="Nenhuma hora nesta frente? Registre a ausência do dia, ou remova a alocação no planejamento."
          >
            <Input
              id="adjust-hours"
              type="number"
              step={0.25}
              min={0.25}
              autoFocus
              value={hours}
              onChange={(event) => setHours(event.target.value)}
            />
          </Field>

          {unit !== undefined && (
            <Field
              label={`Executado (${unit.name})`}
              htmlFor="adjust-quantity"
              hint={
                plannedQuantity === undefined
                  ? 'Quanto saiu de fato nesta frente hoje.'
                  : `Planejado para hoje: ${formatQuantity(plannedQuantity, unit)}.`
              }
            >
              <Input
                id="adjust-quantity"
                type="number"
                step={10 ** -unit.decimals}
                min={0}
                value={quantity}
                onChange={(event) => {
                  setQuantity(event.target.value);
                }}
              />
            </Field>
          )}

          <Field
            label="Motivo"
            htmlFor="adjust-reason"
            required={required}
            hint={
              required
                ? undefined
                : 'Só é exigido se o total do dia passar da jornada, mas fica no histórico se preenchido.'
            }
            {...(errorMessage === undefined ? {} : { error: errorMessage })}
          >
            <textarea
              id="adjust-reason"
              rows={3}
              className="w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-3 py-2 text-sm"
              placeholder="Frente parada por falta de material; remanejado para a estrutura."
              value={justification}
              onChange={(event) => setJustification(event.target.value)}
            />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={isPending || !validHours || !validReason || !validQuantity}
            onClick={() =>
              onConfirm({
                quarterHours: parsed,
                ...(justification.trim() === '' ? {} : { justification: justification.trim() }),
                ...(unit === undefined || parsedQuantity === undefined
                  ? {}
                  : { executedQuantity: parsedQuantity }),
              })
            }
          >
            {isPending ? 'Salvando…' : 'Salvar ajuste'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Ausência: o motivo é obrigatório, e o planejado continua registrado (RN-107). */
export function AbsenceDialog({
  open,
  onOpenChange,
  resourceName,
  plannedQuarterHours,
  isPending,
  errorMessage,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceName: string;
  plannedQuarterHours: number;
  isPending: boolean;
  errorMessage?: string;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registrar ausência</DialogTitle>
          <DialogDescription>
            {resourceName} não trabalhou neste dia. As{' '}
            {Hours.ofQuarters(plannedQuarterHours).format()} planejadas continuam registradas — é
            isso que mostra que havia trabalho previsto e ele não aconteceu.
          </DialogDescription>
        </DialogHeader>

        <Field
          label="Motivo"
          htmlFor="absence-reason"
          required
          {...(errorMessage === undefined ? {} : { error: errorMessage })}
        >
          <Input
            id="absence-reason"
            autoFocus
            value={reason}
            placeholder="Atestado médico"
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={isPending || reason.trim().length < 3}
            onClick={() => onConfirm(reason.trim())}
          >
            {isPending ? 'Registrando…' : 'Registrar ausência'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * O valor guardado é cru: horas ficam em quartos de hora e a data em ISO (ADR-106).
 *
 * Quem lê o histórico é gente, e `36 → 24` não diz nada para quem trabalha com `9h → 6h`.
 * A conversão é de exibição e mora aqui — o registro continua exato.
 */
function formatChangeValue(kind: PlanningChange['kind'], value: string | undefined): string {
  if (value === undefined || value === '') return '—';
  if (kind === 'HOURS' || kind === 'DELETE') {
    const quarters = Number(value);
    return Number.isFinite(quarters) ? Hours.ofQuarters(quarters).format() : value;
  }
  if (kind === 'DATE') {
    const [year, month, day] = value.split('-');
    return year === undefined || month === undefined || day === undefined
      ? value
      : `${day}/${month}/${year}`;
  }
  return value;
}

/**
 * Histórico do dia — Q12 a Q15 em uma lista.
 *
 * Quem alterou, quando, **de → para** e por quê, em ordem cronológica. É a tela que
 * responde "o planejamento foi alterado?" sem que ninguém precise abrir a auditoria do
 * sistema inteiro.
 */
export function ChangeHistoryDialog({
  open,
  onOpenChange,
  resourceName,
  dateLabel,
  changes,
  userNameOf,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceName: string;
  dateLabel: string;
  changes: readonly PlanningChange[];
  userNameOf: (userId: string) => string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Histórico de alterações</DialogTitle>
          <DialogDescription>
            {resourceName} · {dateLabel} ·{' '}
            {changes.length === 0
              ? 'nenhuma alteração'
              : `${String(new Set(changes.map((change) => change.sequence)).size)} alterações`}
          </DialogDescription>
        </DialogHeader>

        {changes.length === 0 ? (
          <p className="py-6 text-center text-sm text-(--color-content-muted)">
            O planejamento deste dia não foi alterado.
          </p>
        ) : (
          <ol className="flex max-h-96 flex-col gap-3 overflow-y-auto">
            {changes.map((change) => (
              <li
                key={change.id}
                className="flex flex-col gap-1 rounded-md border border-(--color-border-subtle) p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="neutral">{change.sequence}ª</Badge>
                  <span className="text-sm font-medium">{CHANGE_KIND_LABEL[change.kind]}</span>
                  <Badge tone={change.source === 'INSPECTION' ? 'info' : 'neutral'}>
                    {CHANGE_SOURCE_LABEL[change.source]}
                  </Badge>
                  <span className="ml-auto text-xs text-(--color-content-muted)">
                    {quandoFoiFeita(change)}
                  </span>
                </div>

                <p className="text-sm">
                  {formatChangeValue(change.kind, change.from)} <span aria-label="para">→</span>{' '}
                  {formatChangeValue(change.kind, change.to)}
                  <span className="ml-2 text-xs text-(--color-content-muted)">
                    por {userNameOf(change.actorUserId)}
                  </span>
                </p>

                {change.justification !== undefined && (
                  <p className="text-sm text-(--color-content-muted)">“{change.justification}”</p>
                )}
              </li>
            ))}
          </ol>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
